import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listLeads,
  getLead,
  createLead,
  updateLead,
} from "@/api/leads";
import type { LeadInput } from "@sales-ops-amplify/shared";

const LEADS_QUERY_KEY = ["leads"] as const;
const ACCOUNTS_QUERY_KEY = ["accounts"] as const;

export function useLeads(filters?: {
  accountId?: string;
  opportunityId?: string;
}) {
  return useQuery({
    queryKey: [...LEADS_QUERY_KEY, filters ?? {}],
    queryFn: () => listLeads(filters),
    staleTime: 30_000,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: [...LEADS_QUERY_KEY, "detail", id],
    queryFn: () => getLead(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadInput) => createLead(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<LeadInput>;
    }) => updateLead(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
    },
  });
}
